import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ExternalLink, BadgeCheck } from "lucide-react";
import { useT } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ rating = 0, size = "w-4 h-4" }) {
  const full = Math.round(rating);
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${rating} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= full ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#2A2A2A] fill-[#2A2A2A]"}`}
          strokeWidth={0}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const t = useT();
  const [googleData, setGoogleData] = useState(null);
  const fallbackQuotes = t.testimonials.items;

  useEffect(() => {
    axios
      .get(`${API}/reviews/google`)
      .then((r) => {
        if (r.data?.success && r.data.data?.reviews?.length) {
          setGoogleData(r.data.data);
        }
      })
      .catch(() => {});
  }, []);

  const usingLive = Boolean(googleData);
  const liveReviews = (googleData?.reviews || []).slice(0, 3);
  const rating = googleData?.rating;
  const ratingCount = googleData?.user_rating_count;

  // Prefer official writereview URL once Place ID is known
  const writeUrl = googleData?.place_id
    ? `https://search.google.com/local/writereview?placeid=${googleData.place_id}`
    : BRAND.writeReviewFallbackUrl;

  return (
    <section data-testid="testimonials-section" className="relative py-24 md:py-32 bg-[#111111] border-y border-[#2A2A2A]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] font-semibold mb-5">
              {t.testimonials.eyebrow}
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F5F5F0]">
              {t.testimonials.titleA}
              <br />
              <span className="text-[#A3A3A3] italic font-normal">{t.testimonials.titleB}</span>
            </h2>
          </div>

          {usingLive && rating != null && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              data-testid="google-rating-summary"
              className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-sm p-6 min-w-[260px]"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                {t.testimonials.ratingLabel}
              </div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-serif-display text-5xl text-[#D4AF37]">{rating.toFixed(1)}</span>
                <span className="text-sm text-[#A3A3A3]">/ 5</span>
              </div>
              <div className="mt-2"><Stars rating={rating} size="w-5 h-5" /></div>
              <div className="mt-3 text-xs text-[#A3A3A3]">
                {ratingCount} {t.testimonials.ratingsCount}
              </div>
            </motion.div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {usingLive
            ? liveReviews.map((r, i) => (
                <motion.figure
                  key={`g-${i}`}
                  data-testid={`google-review-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-sm p-8 md:p-10 flex flex-col"
                >
                  <div className="flex items-center justify-between">
                    <Stars rating={r.rating || 0} />
                    <div className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-[#A3A3A3]">
                      <BadgeCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                      {t.testimonials.verifiedBadge}
                    </div>
                  </div>
                  <blockquote className="mt-6 font-serif-display text-lg md:text-xl text-[#F5F5F0] leading-snug italic flex-1">
                    “{r.text || ""}”
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-[#2A2A2A] flex items-center gap-3">
                    {r.author_photo ? (
                      <img
                        src={r.author_photo}
                        alt={r.author_name || "Reviewer"}
                        referrerPolicy="no-referrer"
                        className="w-9 h-9 rounded-full object-cover border border-[#2A2A2A]"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#2A2A2A] flex items-center justify-center text-[#A3A3A3] text-sm font-semibold">
                        {(r.author_name || "?").slice(0, 1)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#F5F5F0] truncate">
                        {r.author_name || "—"}
                      </div>
                      <div className="text-[11px] tracking-[0.2em] uppercase text-[#A3A3A3] truncate">
                        {r.relative_time || ""}
                      </div>
                    </div>
                  </figcaption>
                </motion.figure>
              ))
            : fallbackQuotes.map((q, i) => (
                <motion.figure
                  key={`f-${i}`}
                  data-testid={`testimonial-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-sm p-8 md:p-10 flex flex-col"
                >
                  <Stars rating={5} />
                  <blockquote className="mt-6 font-serif-display text-xl md:text-2xl text-[#F5F5F0] leading-snug italic flex-1">
                    “{q.q}”
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-[#2A2A2A]">
                    <div className="text-sm font-semibold text-[#F5F5F0]">{q.a}</div>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-[#A3A3A3] mt-1">{q.role}</div>
                  </figcaption>
                </motion.figure>
              ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={writeUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="write-review-cta"
            onClick={() => trackEvent("write_review_click", { source: "testimonials" })}
            className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#C49A26] text-[#0A0A0A] px-7 py-4 rounded-sm font-semibold tracking-wide transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(212,175,55,0.6)]"
          >
            <Star className="w-4 h-4 fill-[#0A0A0A]" strokeWidth={0} />
            {t.testimonials.writeReview}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
